import type { Todo } from '../lib/supabase'

/**
 * タスク完了画像を生成（Instagram/Twitter用）
 */
export const generateTaskCompletionImage = async (
  todo: Todo,
  options: {
    width?: number
    height?: number
    template?: 'minimal' | 'colorful' | 'dark'
  } = {}
): Promise<string> => {
  const { width = 1080, height = 1080, template = 'colorful' } = options

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) throw new Error('Canvas context not available')

  // 背景
  if (template === 'dark') {
    const gradient = ctx.createLinearGradient(0, 0, 0, height)
    gradient.addColorStop(0, '#1a1a2e')
    gradient.addColorStop(1, '#16213e')
    ctx.fillStyle = gradient
  } else if (template === 'colorful') {
    const gradient = ctx.createLinearGradient(0, 0, width, height)
    gradient.addColorStop(0, '#667eea')
    gradient.addColorStop(1, '#764ba2')
    ctx.fillStyle = gradient
  } else {
    ctx.fillStyle = '#ffffff'
  }
  ctx.fillRect(0, 0, width, height)

  // メインテキスト（タスク完了）
  ctx.fillStyle = template === 'minimal' ? '#333333' : '#ffffff'
  ctx.font = 'bold 80px sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('✅ TASK DONE!', width / 2, 200)

  // タスクタイトル
  ctx.font = '48px sans-serif'
  const maxWidth = width - 160
  const words = todo.title.split(' ')
  let line = ''
  let y = 400

  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' '
    const metrics = ctx.measureText(testLine)
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line, width / 2, y)
      line = words[i] + ' '
      y += 60
    } else {
      line = testLine
    }
  }
  ctx.fillText(line, width / 2, y)

  // 進捗バー（もし進捗率が設定されている場合）
  if (todo.progress_percentage > 0) {
    const barWidth = 600
    const barHeight = 30
    const barX = (width - barWidth) / 2
    const barY = height - 300

    // 背景バー
    ctx.fillStyle = template === 'minimal' ? '#e0e0e0' : 'rgba(255, 255, 255, 0.3)'
    ctx.roundRect(barX, barY, barWidth, barHeight, 15)
    ctx.fill()

    // 進捗バー
    ctx.fillStyle = template === 'minimal' ? '#667eea' : '#ffffff'
    const progressWidth = (barWidth * todo.progress_percentage) / 100
    ctx.roundRect(barX, barY, progressWidth, barHeight, 15)
    ctx.fill()

    // パーセンテージ
    ctx.font = 'bold 36px sans-serif'
    ctx.fillStyle = template === 'minimal' ? '#333333' : '#ffffff'
    ctx.fillText(`${todo.progress_percentage}%`, width / 2, barY + 80)
  }

  // フッター（TaStacKロゴ/ハッシュタグ）
  ctx.font = '32px sans-serif'
  ctx.fillStyle = template === 'minimal' ? '#666666' : 'rgba(255, 255, 255, 0.8)'
  ctx.fillText('Created with TaStacK', width / 2, height - 80)

  return canvas.toDataURL('image/png')
}

/**
 * 週次レポート画像を生成
 */
export const generateWeeklyReportImage = async (stats: {
  projectName?: string
  completedTasks: number
  inProgressTasks: number
  pendingTasks: number
  completionRate: number
  projectColor?: string
}): Promise<string> => {
  const width = 1200
  const height = 630 // Twitter card サイズ
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) throw new Error('Canvas context not available')

  // グラデーション背景
  const gradient = ctx.createLinearGradient(0, 0, width, height)
  const color = stats.projectColor || '#667eea'
  gradient.addColorStop(0, color)
  gradient.addColorStop(1, adjustBrightness(color, -30))
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, width, height)

  // タイトル
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 60px sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('📈 週次進捗レポート', width / 2, 100)

  if (stats.projectName) {
    ctx.font = '36px sans-serif'
    ctx.fillText(stats.projectName, width / 2, 160)
  }

  // 統計カード
  const cardY = 220
  const cardWidth = 300
  const cardHeight = 200
  const cardSpacing = 50

  // 完了タスク
  drawStatCard(ctx, 100, cardY, cardWidth, cardHeight, '✅ 完了', stats.completedTasks.toString(), '#4ade80')

  // 進行中タスク
  drawStatCard(ctx, 100 + cardWidth + cardSpacing, cardY, cardWidth, cardHeight, '🔄 進行中', stats.inProgressTasks.toString(), '#60a5fa')

  // 達成率
  drawStatCard(ctx, 100 + (cardWidth + cardSpacing) * 2, cardY, cardWidth, cardHeight, '📊 達成率', `${stats.completionRate}%`, '#f472b6')

  // フッター
  ctx.font = '28px sans-serif'
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
  ctx.textAlign = 'center'
  ctx.fillText('Powered by TaStacK - プロジェクト管理ツール', width / 2, height - 40)

  return canvas.toDataURL('image/png')
}

/**
 * チーム成果画像を生成
 */
export const generateTeamAchievementImage = async (data: {
  projectName: string
  teamSize: number
  completedTasks: number
  duration: number
  projectColor?: string
}): Promise<string> => {
  const width = 1080
  const height = 1080
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) throw new Error('Canvas context not available')

  // 背景
  const gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, width / 2)
  gradient.addColorStop(0, data.projectColor || '#667eea')
  gradient.addColorStop(1, adjustBrightness(data.projectColor || '#667eea', -40))
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, width, height)

  // 中央の円（背景）
  ctx.fillStyle = 'rgba(255, 255, 255, 0.1)'
  ctx.beginPath()
  ctx.arc(width / 2, height / 2, 400, 0, Math.PI * 2)
  ctx.fill()

  // タイトル
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 72px sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('🎊 プロジェクト完了!', width / 2, 150)

  // プロジェクト名
  ctx.font = 'bold 56px sans-serif'
  ctx.fillText(data.projectName, width / 2, 250)

  // 統計情報
  const statsY = 400
  const lineHeight = 80

  ctx.font = '48px sans-serif'
  ctx.fillText(`👥 チームメンバー: ${data.teamSize}名`, width / 2, statsY)
  ctx.fillText(`✅ 完了タスク: ${data.completedTasks}個`, width / 2, statsY + lineHeight)
  ctx.fillText(`📅 期間: ${data.duration}日`, width / 2, statsY + lineHeight * 2)

  // 達成感のエフェクト（星）
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
  for (let i = 0; i < 20; i++) {
    const x = Math.random() * width
    const y = Math.random() * height
    const size = Math.random() * 10 + 5
    ctx.beginPath()
    ctx.arc(x, y, size, 0, Math.PI * 2)
    ctx.fill()
  }

  // フッター
  ctx.font = 'bold 36px sans-serif'
  ctx.fillStyle = '#ffffff'
  ctx.fillText('#TaStacK #チームワーク #達成', width / 2, height - 80)

  return canvas.toDataURL('image/png')
}

/**
 * Instagramストーリー用テンプレート生成
 */
export const generateInstagramStory = async (
  todo: Todo,
  template: 'minimal' | 'gradient' | 'bold' = 'gradient'
): Promise<string> => {
  const width = 1080
  const height = 1920 // Instagram Story サイズ
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) throw new Error('Canvas context not available')

  // 背景スタイル
  if (template === 'gradient') {
    const gradient = ctx.createLinearGradient(0, 0, 0, height)
    gradient.addColorStop(0, '#f093fb')
    gradient.addColorStop(0.5, '#f5576c')
    gradient.addColorStop(1, '#4facfe')
    ctx.fillStyle = gradient
  } else if (template === 'bold') {
    ctx.fillStyle = '#000000'
  } else {
    ctx.fillStyle = '#ffffff'
  }
  ctx.fillRect(0, 0, width, height)

  // 上部装飾
  ctx.fillStyle = template === 'minimal' ? '#667eea' : 'rgba(255, 255, 255, 0.2)'
  ctx.fillRect(0, 0, width, 200)

  // メインコンテンツエリア
  const contentY = height / 2 - 200

  // チェックマークアイコン（大きく）
  ctx.font = '200px sans-serif'
  ctx.fillStyle = template === 'minimal' ? '#667eea' : '#ffffff'
  ctx.textAlign = 'center'
  ctx.fillText('✅', width / 2, contentY)

  // "DONE" テキスト
  ctx.font = 'bold 120px sans-serif'
  ctx.fillText('DONE!', width / 2, contentY + 200)

  // タスクタイトル
  ctx.font = '56px sans-serif'
  ctx.fillStyle = template === 'minimal' ? '#333333' : '#ffffff'
  const maxWidth = width - 160
  wrapText(ctx, todo.title, width / 2, contentY + 320, maxWidth, 70)

  // 進捗バー
  if (todo.progress_percentage >= 100) {
    const barWidth = 800
    const barHeight = 40
    const barX = (width - barWidth) / 2
    const barY = contentY + 500

    ctx.fillStyle = template === 'minimal' ? '#e0e0e0' : 'rgba(255, 255, 255, 0.3)'
    ctx.roundRect(barX, barY, barWidth, barHeight, 20)
    ctx.fill()

    ctx.fillStyle = template === 'minimal' ? '#4ade80' : '#ffffff'
    ctx.roundRect(barX, barY, barWidth, barHeight, 20)
    ctx.fill()
  }

  // 下部ブランディング
  ctx.font = 'bold 48px sans-serif'
  ctx.fillStyle = template === 'minimal' ? '#666666' : 'rgba(255, 255, 255, 0.9)'
  ctx.textAlign = 'center'
  ctx.fillText('TaStacK', width / 2, height - 200)

  ctx.font = '36px sans-serif'
  ctx.fillStyle = template === 'minimal' ? '#999999' : 'rgba(255, 255, 255, 0.7)'
  ctx.fillText('タスク管理アプリ', width / 2, height - 140)

  return canvas.toDataURL('image/png')
}

// ヘルパー関数

function drawStatCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  value: string,
  accentColor: string
) {
  // カード背景
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
  ctx.roundRect(x, y, width, height, 20)
  ctx.fill()

  // アクセントライン
  ctx.fillStyle = accentColor
  ctx.fillRect(x, y, width, 8)

  // ラベル
  ctx.fillStyle = '#666666'
  ctx.font = '32px sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText(label, x + width / 2, y + 70)

  // 値
  ctx.fillStyle = '#333333'
  ctx.font = 'bold 72px sans-serif'
  ctx.fillText(value, x + width / 2, y + 150)
}

function adjustBrightness(color: string, percent: number): string {
  const num = parseInt(color.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = (num >> 16) + amt
  const G = ((num >> 8) & 0x00ff) + amt
  const B = (num & 0x0000ff) + amt
  return (
    '#' +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  )
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) {
  const words = text.split(' ')
  let line = ''
  let currentY = y

  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' '
    const metrics = ctx.measureText(testLine)
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line, x, currentY)
      line = words[i] + ' '
      currentY += lineHeight
    } else {
      line = testLine
    }
  }
  ctx.fillText(line, x, currentY)
}

/**
 * 画像をダウンロード
 */
export const downloadImage = (dataUrl: string, filename: string) => {
  const link = document.createElement('a')
  link.download = filename
  link.href = dataUrl
  link.click()
}
